import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

// Parallax hook
const useParallax = () => {
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return scrollY;
};

// Context-specific CTA configurations
const ctaVariants = {
  default: {
    title: 'Bereit für Ihre',
    highlight: 'kostenlose Bewertung',
    description: 'Starten Sie jetzt und erhalten Sie in wenigen Minuten eine professionelle Einschätzung Ihres Unternehmenswertes.',
    primaryCta: { text: 'Jetzt kostenlos bewerten', href: '/bewertung' },
    secondaryCta: { text: 'Beratungsgespräch vereinbaren', href: '/kontakt' }
  },
  verkauf: {
    title: 'Bereit für den',
    highlight: 'Verkaufsprozess',
    description: 'Starten Sie mit einer fundierten Bewertung und maximieren Sie Ihren Verkaufspreis. Unser Team begleitet Sie bis zum erfolgreichen Abschluss.',
    primaryCta: { text: 'Jetzt Verkaufsprozess starten', href: '/kontakt' },
    secondaryCta: { text: 'Erst Unternehmen bewerten', href: '/bewertung' }
  },
  kauf: {
    title: 'Das richtige',
    highlight: 'Kaufobjekt finden',
    description: 'Prüfen Sie den fairen Wert potenzieller Kaufobjekte und vermeiden Sie Überzahlung. Unsere Experten unterstützen Sie bei der Due Diligence.',
    primaryCta: { text: 'Kaufberatung anfragen', href: '/kontakt' },
    secondaryCta: { text: 'Firmenwert prüfen', href: '/bewertung' }
  },
  beratung: {
    title: 'Individuelle',
    highlight: 'M&A-Beratung',
    description: 'Ob Verkauf, Kauf oder Nachfolge – profitieren Sie von unserer langjährigen Transaktionserfahrung. Erste Beratung unverbindlich.',
    primaryCta: { text: 'Erstgespräch vereinbaren', href: '/kontakt' },
    secondaryCta: { text: 'Kostenlose Bewertung', href: '/bewertung' }
  }
};

const CTASection = ({ variant = 'default' }) => {
  const scrollY = useParallax();
  const config = ctaVariants[variant] || ctaVariants.default;
  
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#FFE5E5]/50 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-[#FF0000]/10 blur-2xl" />
      </div>
      
      {/* Parallax Decorative Elements - MORE VISIBLE */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Circles */}
        <div 
          className="absolute w-40 h-40 rounded-full border-3 border-[#FF0000]/25 hidden lg:block"
          style={{ 
            top: '8%', 
            left: '5%',
            transform: `translateY(${(scrollY - 2500) * 0.2}px)` 
          }}
        />
        <div 
          className="absolute w-28 h-28 rounded-full bg-[#FF0000]/15 hidden lg:block"
          style={{ 
            bottom: '15%', 
            right: '8%',
            transform: `translateY(${(scrollY - 2500) * -0.18}px)` 
          }}
        />
        <div 
          className="absolute w-20 h-20 rounded-full bg-[#FFE5E5]/60 hidden lg:block"
          style={{ 
            top: '50%', 
            left: '10%',
            transform: `translateY(${(scrollY - 2500) * 0.1}px)` 
          }}
        />
        
        {/* Arrows */}
        <img 
          src="/arrow-up-right.webp" 
          alt="" 
          className="absolute w-28 h-auto opacity-30 hidden lg:block"
          style={{ 
            top: '10%', 
            left: '3%',
            transform: `translateY(${(scrollY - 2500) * 0.22}px) rotate(${(scrollY - 2500) * 0.015}deg)` 
          }}
          aria-hidden="true"
        />
        <img 
          src="/arrow-down.webp" 
          alt="" 
          className="absolute w-24 h-auto opacity-25 hidden xl:block"
          style={{ 
            bottom: '10%', 
            right: '5%',
            transform: `translateY(${(scrollY - 2500) * -0.2}px)` 
          }}
          aria-hidden="true"
        />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
          <h2 className="heading-2 mb-6">
            {config.title} <span className="text-[#FF0000]">{config.highlight}</span>?
          </h2>
          <p className="body-large max-w-2xl mx-auto mb-10">
            {config.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to={config.primaryCta.href} 
              className="btn-primary gap-2 text-lg px-8 py-4"
            >
              {config.primaryCta.text}
              <ArrowRight size={20} />
            </Link>
            <Link 
              to={config.secondaryCta.href} 
              className="btn-secondary gap-2"
            >
              {config.secondaryCta.text}
            </Link>
          </div>

          <p className="mt-8 text-[#6b6e6c] text-sm">
            Keine Anmeldung erforderlich • 100% kostenlos • Ergebnis per E-Mail
          </p>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
